import { useEffect, useMemo, useState } from 'react';
import { getPublicTeams } from '../../api/team/teamApi';
import { createTeamJoinRequest } from '../../api/team/joinRequestApi';
import useAutoClearMessage from '../../hooks/useAutoClearMessage';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const DEFAULT_TEAM_LOGO_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" fill="none">
    <rect width="120" height="120" rx="24" fill="#111827"/>
    <circle cx="60" cy="46" r="18" fill="#D1FAE5"/>
    <path d="M30 93c0-14 13-24 30-24s30 10 30 24v3H30v-3z" fill="#A7F3D0"/>
    <text x="60" y="112" text-anchor="middle" fill="#F8FAFC" font-family="Arial, sans-serif" font-size="10" font-weight="700">AH SHOOT</text>
  </svg>`,
);
const DEFAULT_TEAM_LOGO_URL = `data:image/svg+xml;utf8,${DEFAULT_TEAM_LOGO_SVG}`;

function ClientTeamFindPage({ isAuthenticated = false }) {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchField, setSearchField] = useState('TEAM_NAME');
  const [joinPendingTeamIds, setJoinPendingTeamIds] = useState([]);
  const [joinFeedback, setJoinFeedback] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joinModalTeam, setJoinModalTeam] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  useAutoClearMessage(loadError, setLoadError);
  useAutoClearMessage(joinFeedback, setJoinFeedback);
  useAutoClearMessage(joinError, setJoinError);
  useBodyScrollLock(Boolean(joinModalTeam));

  const loadTeams = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const teamItems = await getPublicTeams();
      setTeams(teamItems);
    } catch (error) {
      setTeams([]);
      setLoadError(error?.message || '팀 목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  const filteredTeams = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) {
      return teams;
    }

    return teams.filter((team) => {
      if (searchField === 'ACTIVITY_WEEKDAY') {
        const requestedWeekdays = keyword
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        const teamWeekdays = (Array.isArray(team.activityWeekdays) ? team.activityWeekdays : []).map(
          (weekday) => String(weekday || '').toLowerCase(),
        );

        return requestedWeekdays.every((weekday) =>
          teamWeekdays.some((teamWeekday) => teamWeekday.includes(weekday)),
        );
      }

      const searchableTexts =
        searchField === 'CHAIR_NAME'
          ? [team.chair]
          : [team.name];
      return searchableTexts.some((text) => String(text || '').toLowerCase().includes(keyword));
    });
  }, [searchField, searchKeyword, teams]);

  const handleOpenJoinModal = (team) => {
    const teamId = String(team?.id || '');
    if (!teamId) {
      return;
    }

    if (!isAuthenticated) {
      window.location.replace('/auth/login');
      return;
    }

    if (joinPendingTeamIds.includes(teamId)) {
      return;
    }

    setJoinModalTeam(team);
    setJoinMessage(`${team.name} 팀 가입을 신청합니다.`);
    setJoinFeedback('');
    setJoinError('');
  };

  const handleCloseJoinModal = () => {
    const pendingTeamId = String(joinModalTeam?.id || '');
    if (pendingTeamId && joinPendingTeamIds.includes(pendingTeamId)) {
      return;
    }

    setJoinModalTeam(null);
    setJoinMessage('');
  };

  const handleJoinRequest = async () => {
    const teamId = String(joinModalTeam?.id || '');
    if (!teamId || !joinModalTeam) {
      return;
    }

    if (joinPendingTeamIds.includes(teamId)) {
      return;
    }

    setJoinFeedback('');
    setJoinError('');
    setJoinPendingTeamIds((previous) =>
      previous.includes(teamId) ? previous : [...previous, teamId],
    );

    try {
      await createTeamJoinRequest({
        teamId,
        message: joinMessage.trim(),
      });
      setJoinFeedback(`${joinModalTeam.name} 팀에 가입 신청을 보냈습니다.`);
      setJoinModalTeam(null);
      setJoinMessage('');
    } catch (error) {
      const errorMessage = error?.message || '가입 신청에 실패했습니다.';
      if (error?.status === 400) {
        setJoinModalTeam(null);
        setJoinMessage('');
        window.alert(errorMessage);
        return;
      }
      setJoinError(errorMessage);
    } finally {
      setJoinPendingTeamIds((previous) => previous.filter((id) => id !== teamId));
    }
  };

  return (
    <main
      className="min-h-screen px-6 py-8 md:px-10 md:py-10"
      style={{
        background:
          'radial-gradient(circle at 8% 0%, #dcfce7 0, transparent 30%), radial-gradient(circle at 100% 10%, #dbeafe 0, transparent 34%), linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%)',
      }}
    >
      <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-6">
        <header className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm">
          <div>
            <p className="m-0 inline-block rounded-full bg-[#111827] px-3 py-1 text-xs font-bold tracking-[0.08em] text-white">
              TEAM FIND
            </p>
            <h1 className="mt-3 text-[clamp(26px,4.5vw,36px)] font-semibold leading-tight tracking-[-0.03em] text-[#0f172a]">
              팀 찾기
            </h1>
            <p className="mt-2 text-sm text-[#475467]">
              등록된 팀을 검색하고 기본 정보를 확인한 뒤 바로 가입 신청할 수 있습니다.
            </p>
          </div>
        </header>

        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="m-0 text-lg font-semibold text-[#0f172a]">등록 팀 목록</h2>
            <span className="text-sm font-medium text-[#475467]">
              검색 결과 {filteredTeams.length}개
            </span>
          </div>

          <div className="mt-4">
            <div className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
              <select
                value={searchField}
                onChange={(event) => setSearchField(event.target.value)}
                className="h-11 rounded-xl border border-[#d0d5dd] bg-white px-4 text-sm outline-none focus:border-[#667085]"
              >
                <option value="TEAM_NAME">팀명</option>
                <option value="CHAIR_NAME">회장명</option>
                <option value="ACTIVITY_WEEKDAY">활동요일</option>
              </select>
              <input
                type="text"
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
                className="h-11 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 text-sm outline-none focus:border-[#667085]"
                placeholder={
                  searchField === 'CHAIR_NAME'
                    ? '회장 이름으로 검색'
                    : searchField === 'ACTIVITY_WEEKDAY'
                      ? '예: 수, 토'
                      : '팀 이름으로 검색'
                }
              />
            </div>
          </div>

          {joinFeedback && (
            <p className="mt-5 rounded-2xl border border-[#abefc6] bg-[#ecfdf3] px-4 py-3 text-sm text-[#067647]">
              {joinFeedback}
            </p>
          )}

          {joinError && (
            <p className="mt-5 rounded-2xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
              {joinError}
            </p>
          )}

          <div className="mt-5 space-y-3">
            {isLoading && (
              <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                팀 목록을 불러오는 중입니다...
              </div>
            )}

            {!isLoading && loadError && (
              <div className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                <p className="m-0">{loadError}</p>
                <button
                  type="button"
                  onClick={loadTeams}
                  className="mt-3 h-9 rounded-lg border border-[#fecdca] bg-white px-3 text-sm font-semibold text-[#b42318]"
                >
                  다시 시도
                </button>
              </div>
            )}

            {!isLoading && !loadError && filteredTeams.length === 0 && (
              <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                검색 조건에 맞는 팀이 없습니다.
              </div>
            )}
          </div>

          {!isLoading && !loadError && filteredTeams.length > 0 && (
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {filteredTeams.map((team) => (
                <article
                  key={team.id}
                  className="rounded-2xl border border-[#e4e7ec] bg-[#fcfcfd] p-5"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-16 w-16 overflow-hidden rounded-2xl border border-[#d0d5dd] bg-white shadow-sm">
                      <img
                        src={team.logoUrl || DEFAULT_TEAM_LOGO_URL}
                        alt={`${team.name} 로고`}
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_TEAM_LOGO_URL;
                        }}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="m-0 text-base font-semibold text-[#101828]">{team.name}</h3>
                        <span className="rounded-full bg-[#eef4ff] px-2 py-1 text-xs font-semibold text-[#3538cd]">
                          {team.genderType}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-[#475467]">
                        {team.description || '팀 소개가 아직 등록되지 않았습니다.'}
                      </p>
                    </div>
                  </div>

                  <dl className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-white px-3 py-2">
                      <dt className="text-xs text-[#667085]">활동 요일</dt>
                      <dd className="mt-1 text-sm font-semibold text-[#101828]">
                        {Array.isArray(team.activityWeekdays) && team.activityWeekdays.length > 0
                          ? team.activityWeekdays.join(', ')
                          : '-'}
                      </dd>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <dt className="text-xs text-[#667085]">시간대</dt>
                      <dd className="mt-1 text-sm font-semibold text-[#101828]">
                        {team.timeRange || '-'}
                      </dd>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <dt className="text-xs text-[#667085]">평균 연령</dt>
                      <dd className="mt-1 text-sm font-semibold text-[#101828]">
                        {team.ageBand || '-'}
                      </dd>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <dt className="text-xs text-[#667085]">회장</dt>
                      <dd className="mt-1 text-sm font-semibold text-[#101828]">
                        {team.chair || '-'}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleOpenJoinModal(team)}
                      disabled={joinPendingTeamIds.includes(String(team.id))}
                      className="h-10 rounded-xl bg-[#111827] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
                    >
                      {joinPendingTeamIds.includes(String(team.id))
                        ? '신청 중...'
                        : isAuthenticated
                          ? '가입 신청'
                          : '로그인 후 가입 신청'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {joinModalTeam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
          <div
            className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
            onClick={handleCloseJoinModal}
            aria-hidden="true"
          />
          <section className="relative z-10 w-full max-w-[520px] rounded-3xl border border-[#dbe4f4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">가입 신청</p>
                <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                  {joinModalTeam.name}
                </h3>
                <p className="mt-2 text-sm text-[#667085]">
                  팀 운영진에게 전달될 메시지를 입력해 주세요.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseJoinModal}
                aria-label="가입 신청 모달 닫기"
                className="grid h-9 w-9 place-items-center rounded-lg border border-[#d0d5dd] bg-white text-base font-semibold text-[#344054]"
              >
                X
              </button>
            </div>

            <div className="mt-5">
              <label className="mb-2 block text-sm font-semibold text-[#344054]" htmlFor="team-find-join-message">
                신청 메시지
              </label>
              <textarea
                id="team-find-join-message"
                value={joinMessage}
                onChange={(event) => setJoinMessage(event.target.value)}
                className="min-h-[120px] w-full rounded-2xl border border-[#d0d5dd] px-3 py-3 text-sm outline-none focus:border-[#667085]"
                placeholder="예: 안녕하세요. 토요일 경기 참여를 꾸준히 할 수 있어 가입 신청드립니다."
                maxLength={300}
              />
              <p className="mt-2 text-right text-xs text-[#667085]">
                {joinMessage.length}/300
              </p>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseJoinModal}
                className="h-10 rounded-xl border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleJoinRequest}
                disabled={joinPendingTeamIds.includes(String(joinModalTeam.id))}
                className="h-10 rounded-xl bg-[#111827] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
              >
                {joinPendingTeamIds.includes(String(joinModalTeam.id)) ? '신청 중...' : '신청 보내기'}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default ClientTeamFindPage;
